import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { productStructureApi, type VariantGroup } from '../../api/productstructure';

export function VariantManagerPage() {
  const [items, setItems] = useState<VariantGroup[]>([]);
  const [productId, setProductId] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const reload = () => {
    void productStructureApi.listVariants(productId ? { product_id: productId } : undefined).then(setItems).catch((e) => setError(String(e)));
  };

  useEffect(() => { reload(); }, [productId]);

  const create = async () => {
    if (!productId.trim() || !name.trim()) return;
    try {
      await productStructureApi.createVariant({
        product_id: productId.trim(),
        name: name.trim(),
        dimensions: [{ name: 'Color', type: 'color', options: [{ value: 'Default' }] }],
      });
      setName('');
      reload();
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/product-structure" className="text-sm text-slate-600 hover:underline">← Structure hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Variant Manager</h1>
      <div className="mt-4 flex flex-wrap gap-2">
        <input value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="Product ID filter" className="rounded-lg border px-3 py-2 text-sm" />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New variant group name" className="rounded-lg border px-3 py-2 text-sm" />
        <button type="button" onClick={() => void create()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Create</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <table className="mt-6 min-w-full rounded-xl border bg-white text-sm">
        <thead className="border-b bg-slate-50 text-left text-slate-600">
          <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Product</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Dimensions</th></tr>
        </thead>
        <tbody>
          {items.map((g) => (
            <tr key={g.id} className="border-b">
              <td className="px-4 py-3 font-medium">{g.name}</td>
              <td className="px-4 py-3">{g.product_id}</td>
              <td className="px-4 py-3">{g.status}</td>
              <td className="px-4 py-3">{g.dimensions?.length ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function VariantMatrixPage() {
  const [groups, setGroups] = useState<VariantGroup[]>([]);
  const [groupId, setGroupId] = useState('');
  const [matrix, setMatrix] = useState<{ combinations: { sku?: string; options: Record<string, string> }[] } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    void productStructureApi.listVariants().then((g) => { setGroups(g); if (g[0]?.id) setGroupId(g[0].id); });
  }, []);

  const generate = async () => {
    if (!groupId) return;
    try {
      const m = await productStructureApi.generateVariants({ variant_group_id: groupId });
      setMatrix(m);
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/product-structure" className="text-sm text-slate-600 hover:underline">← Structure hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Variant Matrix</h1>
      <div className="mt-4 flex gap-2">
        <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className="rounded-lg border px-3 py-2 text-sm">
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <button type="button" onClick={() => void generate()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Generate matrix</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {matrix && (
        <div className="mt-6 overflow-x-auto rounded-xl border bg-white">
          <table className="min-w-full text-sm">
            <thead className="border-b bg-slate-50"><tr><th className="px-4 py-3 text-left">SKU</th><th className="px-4 py-3 text-left">Options</th></tr></thead>
            <tbody>
              {matrix.combinations.map((c, i) => (
                <tr key={i} className="border-b">
                  <td className="px-4 py-3 font-mono text-xs">{c.sku ?? '—'}</td>
                  <td className="px-4 py-3">{Object.entries(c.options).map(([k, v]) => `${k}: ${v}`).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
